import { CameraAltOutlined } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import  { type CameraEnhancer, type CameraView, type CaptureVisionRouter } from "dynamsoft-barcode-reader-bundle";
import { useEffect, useRef, useState } from "react";

/** Type alias for the dynamically-imported Dynamsoft module. */
type DynamsoftSdk = typeof import("dynamsoft-barcode-reader-bundle");

const tInstruction =
  "Hold the back of the license about an arm's length from the camera, with the barcode well lit and centered in view.";
const tInitializing = "Initializing scanner…";
const tCancel = "Cancel";
const tUseScan = "Use this scan";
const tDecodedLabel = "Decoded barcode (debug)";
const tDecodedHelper = 'Verify the text looks correct, then click "Use this scan" to populate the form.';
const tInitError = "Could not start the barcode scanner. Please enter the visitor information manually.";
const tPermissionDenied =
  "Camera access was denied. Please allow camera access or enter the visitor information manually.";

/**
 * License key for Dynamsoft Barcode Reader.
 *
 * This is checked into source intentionally: Vite inlines env vars into the client bundle anyway,
 * so any frontend "secret" pattern is theatre — anyone can copy this string out of the deployed
 * JS in dev tools. The actual protection is **domain-locking** the key in the Dynamsoft customer
 * portal (https://www.dynamsoft.com/customer/license/list/). Make sure the allowed origin list
 * covers localhost + every crowdcoursing domain you deploy to; that way a leaked key is useless
 * to anyone running on a different host.
 *
 * To rotate: replace the string below, open a PR, deploy. To use a commercial key in production
 * later, swap this constant — the consumption code doesn't care which kind of key it is.
 */
const DYNAMSOFT_LICENSE =
  "DLS2eyJoYW5kc2hha2VDb2RlIjoiMTA1NjE5MzU1LU1UQTFOakU1TXpVMUxYZGxZaTFVY21saGJGQnliMm8iLCJtYWluU2VydmVyVVJMIjoiaHR0cHM6Ly9tZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwib3JnYW5pemF0aW9uSUQiOiIxMDU2MTkzNTUiLCJzdGFuZGJ5U2VydmVyVVJMIjoiaHR0cHM6Ly9zZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwiY2hlY2tDb2RlIjo3ODEwMjIwMzJ9";

/**
 * The SDK's wasm + worker engine files are fetched from this root at runtime. Using jsDelivr means
 * we don't need to copy / serve the wasm ourselves. For locked-down environments, host these files
 * yourself and point this at your own URL prefix.
 */
const DYNAMSOFT_ENGINE_ROOT = "https://cdn.jsdelivr.net/npm/";

/**
 * Lazily import + initialize the Dynamsoft SDK exactly once per page load. We use a dynamic
 * import (rather than a static one at the top of the file) because the SDK module's top-level
 * code calls `URL.createObjectURL` to materialise its web workers. That API doesn't exist in
 * jsdom, so a static import would crash any test that transitively imports this file — even
 * tests that never render the scanner. With a dynamic import, the SDK only loads when this
 * component actually mounts in a real browser.
 */
let sdkPromise: Promise<DynamsoftSdk> | null = null;
function loadDynamsoftSdk(): Promise<DynamsoftSdk> {
  if (sdkPromise) {
    return sdkPromise;
  }
  sdkPromise = (async () => {
    const sdk = await import("dynamsoft-barcode-reader-bundle");
    sdk.CoreModule.engineResourcePaths.rootDirectory = DYNAMSOFT_ENGINE_ROOT;
    sdk.LicenseManager.initLicense(DYNAMSOFT_LICENSE);
    await sdk.CoreModule.loadWasm(["DBR"]);
    return sdk;
  })();
  return sdkPromise;
}

function translateInitError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === "NotAllowedError" || err.name === "SecurityError") {
      return tPermissionDenied;
    }
  }
  if (err instanceof Error && /permission/i.test(err.message)) {
    return tPermissionDenied;
  }
  return tInitError;
}

export interface VisitorIdScannerProps {
  onScan: (rawBarcodeText: string) => void;
  onCancel: () => void;
}

/**
 * Runs a polling decode loop against the browser's native BarcodeDetector.
 * Returns a stop function. Caller is responsible for ensuring the video is
 * already playing.
 */
function startNativeDetectorLoop(
  Detector: NativeBarcodeDetectorConstructor,
  videoEl: HTMLVideoElement,
  onFrame: () => void,
  onResult: (text: string) => void,
): () => void {
  const detector = new Detector({ formats: ["pdf417", "qr_code", "data_matrix", "aztec"] });
  let stopped = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const tick = async () => {
    if (stopped) {
      return;
    }
    onFrame();
    try {
      const codes = await detector.detect(videoEl);
      if (!stopped && codes.length > 0) {
        onResult(codes[0].rawValue);
      }
    } catch {
      // Native decoder can throw intermittently on a bad frame; just keep going.
    }
    if (!stopped) {
      timeoutId = setTimeout(() => void tick(), 200);
    }
  };

  void tick();

  return () => {
    stopped = true;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  };
}

export function VisitorIdScanner({ onScan, onCancel }: VisitorIdScannerProps) {
  const cameraHostRef = useRef<HTMLDivElement | null>(null);
  // Latest onScan in a ref so we can keep the effect's deps empty and avoid tearing down the
  // entire scanner stack on every parent re-render.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [decodedText, setDecodedText] = useState<string | null>(null);
  const [activeDecoder, setActiveDecoder] = useState<string | null>(null);

  useEffect(() => {
    if (!cameraHostRef.current) {
      return;
    }

    let cancelled = false;
    let router: CaptureVisionRouter | null = null;
    let enhancer: CameraEnhancer | null = null;
    let view: CameraView | null = null;
    let attachedUi: HTMLElement | null = null;

    const handleFrame = () => setFramesAnalyzed((n) => n + 1);
    const handleResult = (text: string) => setDecodedText((prev) => (prev === text ? prev : text));

    const startup = async () => {
      try {
        const sdk = await loadDynamsoftSdk();
        if (cancelled) {
          return;
        }

        router = await sdk.CaptureVisionRouter.createInstance();
        view = await sdk.CameraView.createInstance();
        enhancer = await sdk.CameraEnhancer.createInstance(view);

        if (cancelled || !cameraHostRef.current) {
          return;
        }

        // Trim the default CameraView chrome — we don't want the camera / resolution dropdowns.
        const ui = view.getUIElement();
        ui.querySelector(".dce-sel-camera")?.remove();
        ui.querySelector(".dce-sel-resolution")?.remove();
        // Dynamsoft's UI root has no intrinsic size; stretch it to fill our host box.
        ui.style.position = "absolute";
        ui.style.inset = "0";
        ui.style.width = "100%";
        ui.style.height = "100%";
        cameraHostRef.current.append(ui);
        attachedUi = ui;

        router.setInput(enhancer);

        // Restrict to driver's-license PDF417 + QR code (so we can sanity-test with a QR).
        const settings = await router.getSimplifiedSettings("ReadSingleBarcode");
        settings.barcodeSettings.barcodeFormatIds = sdk.EnumBarcodeFormat.BF_PDF417 | sdk.EnumBarcodeFormat.BF_QR_CODE;
        await router.updateSettings("ReadSingleBarcode", settings);

        // De-dup so a single barcode held up doesn't keep re-firing the receiver.
        const filter = new sdk.MultiFrameResultCrossFilter();
        filter.enableResultDeduplication("barcode", true);
        await router.addResultFilter(filter);

        await router.addResultReceiver({
          onDecodedBarcodesReceived: (result) => {
            const item = result.barcodeResultItems?.[0];
            if (!item) {
              return;
            }
            setDecodedText((prev) => (prev === item.text ? prev : item.text));
          },
        });

        await enhancer.open();
        if (cancelled) {
          return;
        }
        await router.startCapturing("ReadSingleBarcode");

        if (!nativeStarted) {
          const reader = buildReader();
          const controls: IScannerControls = await reader.decodeFromVideoElement(videoRef.current, (result) => {
            handleFrame();
            if (result) {
              handleResult(result.getText());
            }
          });
          stopLoop = () => controls.stop();
          setActiveDecoder("ZXing");
        }

        if (cancelled) {
          return;
        }
        setIsReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(translateInitError(err));
        }
      }
    };

    void startup();

    return () => {
      cancelled = true;
      // Dynamsoft tear-down is async, but we don't need to await it; fire and forget.
      void (async () => {
        try {
          await router?.stopCapturing();
        } catch {
          // ignore
        }
        try {
          enhancer?.close();
        } catch {
          // ignore
        }
        try {
          router?.dispose();
        } catch {
          // ignore
        }
        try {
          enhancer?.dispose();
        } catch {
          // ignore
        }
        try {
          view?.dispose();
        } catch {
          // ignore
        }
        if (attachedUi?.parentElement) {
          attachedUi.parentElement.removeChild(attachedUi);
        }
      })();
    };
  }, []);

  const handleUseScan = () => {
    if (decodedText) {
      onScanRef.current(decodedText);
    }
  };

  return (
    <Stack gap={2}>
      <Typography color="text.secondary" variant="body2">
        {tInstruction}
      </Typography>

      <Box
        ref={cameraHostRef}
        sx={{
          aspectRatio: "4 / 3",
          bgcolor: "common.black",
          borderRadius: 1,
          maxHeight: { sm: 480, xs: 360 },
          overflow: "hidden",
          position: "relative",
          width: "100%",
        }}
      >
        {!isReady && !error ? (
          <Stack
            alignItems="center"
            gap={1}
            sx={{
              color: "common.white",
              inset: 0,
              justifyContent: "center",
              position: "absolute",
              zIndex: 1,
            }}
          >
            <CircularProgress color="inherit" size={24} />
            <Typography variant="body2">{tInitializing}</Typography>
          </Stack>
        ) : null}
      </Box>

      {error ? <Alert severity="warning">{error}</Alert> : null}

      <TextField
        fullWidth
        helperText={tDecodedHelper}
        InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
        label={tDecodedLabel}
        maxRows={6}
        minRows={3}
        multiline
        value={decodedText ?? ""}
      />

      <Stack direction={{ sm: "row", xs: "column" }} gap={1} justifyContent="flex-end">
        <Button onClick={onCancel} variant="outlined">
          {tCancel}
        </Button>
        <Button disabled={!decodedText} onClick={handleUseScan} startIcon={<CameraAltOutlined />} variant="contained">
          {tUseScan}
        </Button>
      </Stack>
    </Stack>
  );
}
